import { useAuth } from "@/app/auth";
import { deferRelogin, setUnlockPrompt } from "@/app/relogin";
import type { ReloginSecret } from "@/unlock";
import { useCallback, useEffect, useRef, useState } from "react";
import { UnlockOverlay } from "./UnlockOverlay";

type Resolve = (secret: ReloginSecret | null) => void;

export function UnlockGate() {
  const { signOutFromDevice } = useAuth();
  const [asking, setAsking] = useState(false);
  const resolver = useRef<Resolve | null>(null);

  useEffect(() => {
    setUnlockPrompt(
      () =>
        new Promise<ReloginSecret | null>((resolve) => {
          resolver.current = resolve;
          setAsking(true);
        }),
    );
    return () => {
      setUnlockPrompt(null);
      const pending = resolver.current;
      resolver.current = null;
      pending?.(null);
    };
  }, []);

  const settle = useCallback((secret: ReloginSecret | null) => {
    const resolve = resolver.current;
    resolver.current = null;
    setAsking(false);
    resolve?.(secret);
  }, []);

  const onDefer = useCallback(() => {
    deferRelogin();
    settle(null);
  }, [settle]);

  const giveUp = useCallback(() => {
    void signOutFromDevice().finally(() => settle(null));
  }, [signOutFromDevice, settle]);

  if (!asking) {
    return null;
  }

  return <UnlockOverlay onUnlocked={settle} onDefer={onDefer} onGiveUp={giveUp} />;
}
